import backfaceFixed from './BackfaceFixed';

/**
 * @param {Object} Option
 * @param {string} Option.modal - モーダルのクラス名
 * @param {string} Option.open - モーダルを開くボタンのクラス名
 * @param {string} Option.html - htmlタグに付与するクラス名
 * @param {string} Option.globalContainer - グローバルコンテナー（モーダルはコンテナ外に記述してください）
 * @param {any} [Option.close] - ある場合はクラス名を指定
 * @param {any} [Option.overlay] - ある場合はクラス名を指定
 *
 * @example
 	<div class="cModal">
		<div class="cModal__inner js-menu" aria-hidden="true">
			<div class="cModal__wrapper" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="dialog-title">
				<span id="dialog-title" class="visuallyHidden">これはモーダルです</span>

				<div class="cModal__overlay js-modal-overlay"></div>

				<div class="cModal__content" role="document">#contents</div>
			</div>
		</div>
	</div>
 */
export class Modal {
	constructor({
		modal,
		open,
		html,
		globalContainer,
		close = '',
		overlay = '',
	}) {
		this.html = html;
		this.modal = document.querySelector(modal);
		this.container = document.querySelector(globalContainer);
		this.open = document.querySelectorAll(open);
		this.close = document.querySelectorAll(close);
		this.overlay = document.querySelectorAll(overlay);

		if (!this.modal || !this.container) {
			console.error('Modal.js: modal or container is not found');
			return;
		}

		// tabbableな要素は Micromodal.js を参考に実装
		// https://github.com/ghosh/Micromodal/blob/master/lib/src/index.js
		this.FOCUSABLE_ELEMENTS = [
			'a[href]',
			'area[href]',
			'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
			'select:not([disabled]):not([aria-hidden])',
			'textarea:not([disabled]):not([aria-hidden])',
			'button:not([disabled]):not([aria-hidden])',
			'iframe',
			'object',
			'embed',
			'[contenteditable]',
			'[tabindex]:not([tabindex^="-"])',
		];

		this.modalLinkArry = this.modal.querySelectorAll(
			this.FOCUSABLE_ELEMENTS.join(',')
		);

		this.focusLinkArry = document.querySelectorAll(
			this.FOCUSABLE_ELEMENTS.join(',')
		);

		// flag
		this.isModalOpen = false;
	}

	init() {
		this.modalLinkArry?.forEach((el) => {
			el.setAttribute('tabindex', '-1');
		});

		this.modal.setAttribute('inert', '');

		this.modal.addEventListener('keydown', this.handleKeyDown);

		if (this.close.length > 0) {
			this.open.forEach((el) => {
				el.addEventListener('click', () => this.openModal());
			});

			this.close.forEach((el) => {
				el.setAttribute('tabindex', '-1');
				el.setAttribute('aria-label', '閉じる');

				el.addEventListener('click', () => this.closeModal());
			});
		} else {
			this.open.forEach((el) => {
				el.addEventListener('click', () =>
					this.isModalOpen ? this.closeModal() : this.openModal()
				);
			});
		}

		if (this.overlay.length > 0) {
			this.overlay.forEach((el) => {
				el.addEventListener(
					'click',
					(e) => {
						if (e.target === e.currentTarget) {
							this.closeModal();
						}
					},
					false
				);
			});
		}

		this.modal.addEventListener('keydown', (e) => this.handleKeyDown(e));

		window.addEventListener('keydown', (e) => {
			if ((e.key === 'Escape' || e.key === 'Esc') && this.isModalOpen) {
				this.closeModal();
			}
		});
	}

	handleKeyDown(e) {
		const firstFocusable = this.modalLinkArry[0];
		const lastFocusable = this.modalLinkArry[this.modalLinkArry.length - 1];

		if (e.key === 'Tab') {
			if (e.shiftKey) {
				if (document.activeElement === firstFocusable) {
					e.preventDefault();
					firstFocusable instanceof HTMLElement &&
						firstFocusable.focus();
				}
			} else {
				if (document.activeElement === lastFocusable) {
					e.preventDefault();
					firstFocusable instanceof HTMLElement &&
						firstFocusable.focus();
				}
			}
		}
	}

	openModal() {
		// 背景固定
		backfaceFixed(true);

		document.documentElement.classList.add(this.html);

		this.modal.setAttribute('aria-hidden', 'false');
		this.modal.removeAttribute('inert');

		this.container.setAttribute('aria-hidden', 'true');
		this.container.setAttribute('inert', '');

		this.focusLinkArry.forEach((el) => {
			el.setAttribute('tabindex', '-1');
		});

		this.modalLinkArry.forEach((el) => {
			el.removeAttribute('tabindex');
		});

		if (this.close.length > 0) {
			this.open.forEach((el) => {
				el.setAttribute('tabindex', '-1');
			});

			this.close.forEach((el) => {
				el.removeAttribute('tabindex');
			});
		} else {
			this.open.forEach((el) => {
				el.removeAttribute('tabindex');
				el.setAttribute('aria-label', '閉じる');
			});
		}

		this.isModalOpen = true;
	}

	closeModal() {
		// 背景固定解除
		backfaceFixed(false);

		document.documentElement.classList.remove(this.html);

		this.modal.setAttribute('aria-hidden', 'true');
		this.modal.setAttribute('inert', '');

		this.container.setAttribute('aria-hidden', 'false');
		this.container.removeAttribute('inert');

		this.focusLinkArry.forEach((el) => {
			el.removeAttribute('tabindex');
		});

		this.modalLinkArry.forEach((el) => {
			el.setAttribute('tabindex', '-1');
		});

		if (this.close.length > 0) {
			this.open.forEach((el) => {
				el.removeAttribute('tabindex');
				el.setAttribute('aria-label', '開く');
			});

			this.close.forEach((el) => {
				el.setAttribute('tabindex', '-1');
			});
		} else {
			this.open.forEach((el) => {
				el.removeAttribute('tabindex');
				el.setAttribute('aria-label', '開く');
			});
		}

		this.isModalOpen = false;
	}
}
